/**
 * Read a v2 endpoint and throw when it did not work.
 *
 * `if (d.success) setX(...)` with no else turned every failure into an empty
 * state: on the deployed dev site the PIN cookie was not sent, every gated
 * route answered 401, and Ratkaisut said "Vipupisteitä ei ole vielä johdettu"
 * about a map that had seventeen. A failed read throws here, with the server's
 * own message, so the screen's ErrorBox shows what actually happened.
 */
export async function v2Json(url, init = {}) {
  const res = await fetch(url, { credentials: 'include', ...init });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || data.success === false) {
    const message = data?.error?.message || data?.error || `HTTP ${res.status}`;
    const err = new Error(typeof message === 'string' ? message : JSON.stringify(message));
    err.status = res.status;
    err.code = data?.error?.code || null; // e.g. STALE_VERSION, JUDGEMENTS_LOCKED
    throw err;
  }
  return data;
}

/**
 * One dialogue turn, streamed: resolves to the same body v2Json would, and
 * calls onText(field, delta) while the model writes it.
 *
 * The server answers with SSE (text* → done | error) when asked, or with plain
 * JSON when it has nothing to stream — a cached first turn. Both land here.
 * A stream that ends without `done` is a failure, not a short answer: the
 * half-written text is never passed off as the reply.
 */
export async function v2Stream(url, body, onText) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
  });
  if (!String(res.headers.get('content-type') || '').includes('text/event-stream')) {
    const data = await res.json().catch(() => null);
    if (!res.ok || !data || data.success === false) {
      const message = data?.error?.message || data?.error || `HTTP ${res.status}`;
      const err = new Error(typeof message === 'string' ? message : JSON.stringify(message));
      err.status = res.status;
      err.code = data?.error?.code || null;
      throw err;
    }
    return data;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop();
    for (const frame of frames) {
      if (!frame.startsWith('data: ')) continue;
      const { event, data } = JSON.parse(frame.slice(6));
      if (event === 'text') onText?.(data.field, data.delta);
      else if (event === 'done') return data;
      else if (event === 'error') throw new Error(data?.message || 'Stream failed');
    }
  }
  throw new Error('The answer was cut off before it finished.');
}
